import { useOrganization } from '@/auth/AuthContext';
import { Skeleton, Text, Stack, useDisclosure, HStack, Input } from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { DataTableColumnDef as ColumnDef, DataTableCellContext as CellContext, DataTableRow as Row } from 'src/components/DataTable';
import { ProjectSummary } from 'src/api/ProjectSummary';
import ConfirmDialog from 'src/components/ConfirmDialog';
import DataTable, { DataTableToolbar, amountHeader, dateCell, valueCurrencyText } from 'src/components/DataTable';
import IdAndNameData from 'src/data/IdAndNameData';
import { projectService } from 'src/service/ProjectService';
import CountryFlag from 'src/components/CountryFlag';
import { countries } from 'src/data/Countries';
import Content from 'src/components/Content';
import AddButton from 'src/components/buttons/AddButton';
import WaitingSpinner from '@/components/WaitingSpinner';
import { DropDownMenu } from '@/components/DropDownMenu';
import PartnerLink from '@/components/PartnerLink';
import { toaster } from '@/components/ui/toaster';
import ActiveOnlySwitch from '@/components/ActiveOnlySwitch';
import { useI18n } from '@/i18n/I18nContext';

const OverviewProjects = () => {
    const { t } = useI18n();
    const organization = useOrganization();
    const homeCurrency = organization?.currency;
    const [isLoading, setIsLoading] = useState(true);
    const [data, setData] = useState<ProjectSummary[]>([]);
    const [partners, setPartners] = useState<Map<number, string>>(new Map());
    const [activeProjectIds, setActiveProjectIds] = useState<Set<number>>(new Set());
    const [deleteData, setDeleteData] = useState<IdAndNameData<number> | null>(null);
    const [nameFilter, setNameFilter] = useState('');
    const [showActive, setShowActive] = useState(true);

    const { open: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();

    const navigate = useNavigate();

    const onDelete = useCallback((id: number, name: string): Promise<void> => {
        console.info(`deleting ${id} ${name}`);
        return projectService.remove(id)
            .then(() => {
                setData(prev => prev.filter(item => item.project.id !== id));
                setDeleteData(null);
                onDeleteClose();
                toaster.create({
                    title: t('projects.overview.deleted'),
                    type: 'success'
                });
            });
    }, [onDeleteClose, t]);

    const onEdit = useCallback((id: number): void => {
        console.info(`editing ${id}`);
        navigate(`/projects/edit/${id}`);
    }, [navigate]);

    const onView = useCallback((id: number): void => {
        console.info(`viewing ${id}`);
        navigate(`/projects/view/${id}`);
    }, [navigate]);

    useEffect(() => {
        setIsLoading(true);
        projectService
            .listAll()
            .then(r => {
                const partnerId2Name = new Map(r.partners.map(obj => [obj.id, obj.name]));
                setPartners(partnerId2Name);
                setActiveProjectIds(new Set(r.activeProjectIds));
                setData(r.results.concat());
            })
            .finally(() => setIsLoading(false));

    }, []);

    const onFilter = (row: ProjectSummary): boolean => {
        const matchesActive = !showActive || activeProjectIds.has(row.project.id);
        const check = nameFilter.trim().toLowerCase();
        const matchesName = check.length === 0 || row.project.name.trim().toLowerCase().includes(check);
        return matchesActive && matchesName;
    }

    const columns: ColumnDef<ProjectSummary>[] = useMemo(
        () => [
            {
                id: 'country',
                header: t('common.country.label'),
                accessorKey: 'project.country',
                cell: (info: CellContext<ProjectSummary, string>) => (
                    <HStack>
                        <CountryFlag code={info.getValue()} />
                        <Text>{countries[info.getValue()]}</Text>
                    </HStack>
                ),
                sortFn: (rowA: Row<ProjectSummary>, rowB: Row<ProjectSummary>, _: any): number => {
                    const a = rowA.original;
                    const b = rowB.original;
                    let ap = countries[a.project.country] ?? '';
                    let bp = countries[b.project.country] ?? '';
                    return ap.trim().localeCompare(bp.trim(), undefined, { sensitivity: 'base' });
                },
            },
            {
                id: 'name',
                header: t('projects.overview.columns.name'),
                accessorFn: (row: ProjectSummary) => row.project.name.trim(),
                cell: (info: CellContext<ProjectSummary, string>) => (
                    <Text minW='8em' fontWeight={'bold'}>{info.getValue()}</Text>
                ),
            },
            {
                id: 'partner',
                header: t('projects.overview.columns.partner'),
                accessorKey: 'partnerIds',
                cell: (info: CellContext<ProjectSummary, number[]>) => <PartnerLink partnerIds={info.getValue()} partners={partners} keyPrefix='{info.row.original.project.id}' />,
                sortFn: (rowA: Row<ProjectSummary>, rowB: Row<ProjectSummary>, _): number => {
                    const a = rowA.original;
                    const b = rowB.original;
                    const ap = partners.get(a.partnerIds[0])?.trim() ?? '';
                    const bp = partners.get(b.partnerIds[0])?.trim() ?? '';
                    return ap.localeCompare(bp);
                },
            },
            {
                id: 'startDate',
                header: t('projects.overview.columns.startDate'),
                accessorKey: 'project.startDate',
                cell: dateCell()
            },
            {
                id: 'budget',
                header: amountHeader({ name: t('projects.overview.columns.budget'), width: '10rem' }),
                accessorFn: (row: ProjectSummary) => row.budget.reduce((a, yb) => a + (yb.budget ?? 0), 0),
                cell: (info: CellContext<ProjectSummary, number>) => valueCurrencyText(info.getValue(), info.row.original.project.currency, '10rem', 'normal'),
            },
            {
                id: 'budgetChf',
                header: amountHeader({ name: t('projects.overview.columns.projectBudget', { currency: homeCurrency ?? '' }), width: '8rem' }),
                accessorFn: (row: ProjectSummary) => row.budget.reduce((a, yb) => a + (yb.projectBudget ?? 0), 0),
                cell: (info: CellContext<ProjectSummary, number>) => valueCurrencyText(info.getValue(), homeCurrency, '8rem', 'normal'),
            },
            {
                id: 'fundraisingChf',
                header: amountHeader({ name: t('projects.overview.columns.fundraisingBudget', { currency: homeCurrency ?? '' }), width: '8rem' }),
                accessorFn: (row: ProjectSummary) => row.budget.reduce((a, yb) => a + (yb.fundraisingBudget ?? 0), 0),
                cell: (info: CellContext<ProjectSummary, number>) => valueCurrencyText(info.getValue(), homeCurrency, '8rem', 'normal'),
            },
            {
                id: 'edit',
                enableSorting: false,
                cell: info => {
                    const row = info.row.original;
                    return (
                        <DropDownMenu onMenuView={() => onView(row.project.id)} onMenuEdit={() => onEdit(row.project.id)} onMenuDelete={() => {
                            let da: IdAndNameData<number> = {
                                id: row.project.id,
                                name: row.project.name
                            };
                            setDeleteData(da);
                            onDeleteOpen();
                        }} />
                    )
                }
            }
        ], [partners, onDeleteOpen, onEdit, onView, homeCurrency, t]
    );

    return (
        <Content
            title={t('projects.overview.title')}
            description={t('projects.overview.description')}
            actions={<AddButton caption={t('projects.overview.create')} onClick={() => navigate('/projects/add')} />}
        >
            <Stack gap='6'>
                <DataTableToolbar
                    resultCount={isLoading || !organization ? undefined : data.filter(onFilter).length}
                    filtersActive={nameFilter.trim() !== '' || showActive}
                    onClearFilters={() => { setNameFilter(''); setShowActive(false); }}
                >
                    <Input aria-label={t('projects.overview.search')} placeholder={t('projects.overview.search')}
                        w={{ base: 'full', md: '20rem' }} maxW='full' value={nameFilter} onChange={ev => setNameFilter(ev.currentTarget.value)} />
                    <ActiveOnlySwitch checked={showActive} onChange={setShowActive} label={t('projects.overview.activeOnly')} />
                </DataTableToolbar>
                {isLoading || !organization ? (
                    <Stack py={5}>
                        <WaitingSpinner size='xl' />
                        <Skeleton height='20px' />
                        <Skeleton height='20px' />
                        <Skeleton height='20px' />
                    </Stack>
                ) : (
                    <>
                        <DataTable columns={columns} data={data}
                            hideColumnsOnXs={['country', 'startDate', 'budget', 'budgetChf', 'fundraisingChf', 'edit']}
                            onRowClick={row => onView(row.project.id)}
                            initialSortByColumn={'country'} initialSortByDesc={false}
                            onFilter={onFilter} filter={`${showActive ? 'active' : 'all'}:${nameFilter.trim()}`}
                        />
                        <ConfirmDialog
                            heading={t('projects.overview.deleteHeading', { name: deleteData?.name ?? '' })}
                            isOpen={isDeleteOpen}
                            onClose={onDeleteClose}
                            onConfirm={async (): Promise<void> => onDelete(deleteData.id, deleteData.name)}
                        />
                    </>
                )}
            </Stack>
        </Content>
    );

}

export default OverviewProjects
